/**
 * Potvrdenie nevratného odhodenia karty — za boost (manéver, súboj) aj na limit
 * ruky či kvôli efektu. Odhodená karta je preč: jeden nepozorný klik v ruke by
 * hráča pripravil o kartu, ktorú chcel zahrať.
 */

import type { CardDef } from '@unmatched/engine';
import { useEffect } from 'react';

import { Card } from './Card.js';

/**
 * `boostManeuver` = boost k pohybu, `boostCombat` = k hodnote karty v súboji,
 * `handLimit` = odhodenie na limit ruky, `effect` = odhodenie, ktoré chce efekt karty.
 */
export type ConfirmKind = 'boostManeuver' | 'boostCombat' | 'handLimit' | 'effect';

interface Props {
  def: CardDef;
  kind: ConfirmKind;
  /** Pri odhadzovaní na limit: koľko kariet ešte treba odhodiť (vrátane tejto). */
  remaining?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CardConfirm({ def, kind, remaining, onConfirm, onCancel }: Props) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
      if (event.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onConfirm, onCancel]);

  const boost = kind === 'boostManeuver' || kind === 'boostCombat';
  // Houdiniho karty majú pri odhodení za boost vlastný efekt — hráč o ňom má vedieť.
  const boostedWith = boost && /BOOSTED WITH/i.test(def.text);

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="cardConfirmTitle" onClick={onCancel}>
      <div className="modal__panel cardConfirm" onClick={(event) => event.stopPropagation()}>
        <div className="cardConfirm__card">
          <Card def={def} />
        </div>
        <div className="cardConfirm__body">
          <span className={`tag ${boost ? 'tag--primary' : 'tag--danger'}`}>{boost ? 'Boost' : 'Odhodenie'}</span>
          <h2 id="cardConfirmTitle" className="modal__title">
            Odhodiť {def.name}?
          </h2>
          <p className="cardConfirm__what">
            {kind === 'boostManeuver' ? (
              <>
                Pohyb v tomto manévri <b>+{def.boost}</b>.{' '}
              </>
            ) : kind === 'boostCombat' ? (
              <>
                Hodnota tvojej karty v súboji <b>+{def.boost}</b>.{' '}
              </>
            ) : kind === 'handLimit' ? (
              <>
                Máš viac kariet, než je limit ruky
                {remaining && remaining > 1 ? ` — odhodiť treba ešte ${remaining}` : ''}.{' '}
              </>
            ) : null}
            Karta ide do odhodu a zahrať ju už nebudeš môcť.
          </p>
          {boostedWith ? (
            <p className="cardConfirm__note">Spustí sa aj jej efekt „BOOSTED WITH".</p>
          ) : null}
          <div className="cardConfirm__actions">
            <button type="button" className="btn btn--ghost" onClick={onCancel}>
              Ponechať
            </button>
            <button type="button" className="btn btn--accent" onClick={onConfirm} autoFocus>
              {boost ? `Odhodiť za +${def.boost}` : 'Odhodiť'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
